import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { Link } from 'react-router-dom'
import styles from './AppNoTenantLanding.module.css'
import { applyMarketingLandingDocumentTheme } from '@/utils/theme'
import {
  AGENCY_WHATSAPP_LABEL,
  CONTACT_EMAIL,
  DAILY_COMPARE,
  MONTHLY_PRICE,
  SOLONLINE_SITE_URL,
  WA_APP,
  WA_DUVIDA,
  WA_ESPACO,
} from '@/config/sales'

const IMG = {
  salon: '/landing/salon.jpg',
  clinic: '/landing/clinic.jpg',
  barber: '/landing/barber.jpg',
  physio: '/landing/physio.jpg',
  gym: '/landing/gym.jpg',
  workshop: '/landing/workshop.jpg',
  tutor: '/landing/tutor.jpg',
} as const

const MONTHLY_NUMBER = Number(MONTHLY_PRICE.replace(',', '.')) || 9.7
const MAIL_CONTACT = `mailto:${CONTACT_EMAIL}`

const cssVars = (vars: Record<string, string | number>) => vars as CSSProperties

/* ---------------------------------------------------------------- ícones */

type IconName =
  | 'arrow'
  | 'check'
  | 'x'
  | 'plus'
  | 'menu'
  | 'calendar'
  | 'bell'
  | 'dashboard'
  | 'phone'
  | 'tasks'
  | 'qr'
  | 'scissors'
  | 'stethoscope'
  | 'dumbbell'
  | 'activity'
  | 'sparkles'
  | 'wrench'
  | 'grad'
  | 'camera'
  | 'briefcase'
  | 'shield'
  | 'users'
  | 'clock'
  | 'euro'
  | 'link'
  | 'store'
  | 'instagram'
  | 'mail'
  | 'globe'

const ICONS: Record<IconName, ReactNode> = {
  arrow: (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  x: (
    <>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </>
  ),
  plus: (
    <>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4" />
      <path d="M8 2v4" />
      <path d="M3 10h18" />
    </>
  ),
  bell: (
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </>
  ),
  phone: (
    <>
      <rect x="5" y="2" width="14" height="20" rx="2.5" />
      <path d="M12 18h.01" />
    </>
  ),
  tasks: (
    <>
      <path d="m3 17 2 2 4-4" />
      <path d="m3 7 2 2 4-4" />
      <path d="M13 6h8" />
      <path d="M13 12h8" />
      <path d="M13 18h8" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="5" height="5" rx="1" />
      <rect x="16" y="3" width="5" height="5" rx="1" />
      <rect x="3" y="16" width="5" height="5" rx="1" />
      <path d="M21 16h-3a2 2 0 0 0-2 2v3" />
      <path d="M21 21v.01" />
      <path d="M12 7v3a2 2 0 0 1-2 2H7" />
      <path d="M3 12h.01" />
      <path d="M12 3h.01" />
      <path d="M12 16v.01" />
      <path d="M16 12h1" />
      <path d="M21 12v.01" />
      <path d="M12 21v-1" />
    </>
  ),
  scissors: (
    <>
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M20 4 8.12 15.88" />
      <path d="M14.47 14.48 20 20" />
      <path d="M8.12 8.12 12 12" />
    </>
  ),
  stethoscope: (
    <>
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 6 6 6 6 0 0 0 6-6v-4" />
      <circle cx="20" cy="10" r="2" />
    </>
  ),
  dumbbell: (
    <>
      <path d="m6.5 6.5 11 11" />
      <path d="m21 21-1-1" />
      <path d="m3 3 1 1" />
      <path d="m18 22 4-4" />
      <path d="m2 6 4-4" />
      <path d="m3 10 7-7" />
      <path d="m14 21 7-7" />
    </>
  ),
  activity: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  sparkles: (
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
  ),
  wrench: (
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  ),
  grad: (
    <>
      <path d="M22 10v6" />
      <path d="M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </>
  ),
  camera: (
    <>
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </>
  ),
  briefcase: (
    <>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </>
  ),
  shield: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  euro: (
    <>
      <path d="M4 10h12" />
      <path d="M4 14h9" />
      <path d="M19 6a7.7 7.7 0 0 0-5.2-2A7.9 7.9 0 0 0 6 12c0 4.4 3.5 8 7.8 8 2 0 3.8-.8 5.2-2" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </>
  ),
  store: (
    <>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </>
  ),
  instagram: (
    <>
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <path d="M17.5 6.5h.01" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </>
  ),
}

function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {ICONS[name]}
    </svg>
  )
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.07c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35m-5.42 7.4h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.89-9.88a9.82 9.82 0 0 1 9.88 9.89c0 5.45-4.44 9.88-9.88 9.88m8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89a11.82 11.82 0 0 0-3.48-8.41Z" />
    </svg>
  )
}

function Stars({ className }: { className?: string }) {
  return (
    <span className={className} role="img" aria-label="5 estrelas">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </span>
  )
}

/* ---------------------------------------------------------------- hooks */

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

function useInView<T extends Element>(once = true, threshold = 0.15) {
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          if (once) io.disconnect()
        } else if (!once) {
          setInView(false)
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [once, threshold])
  return { ref, inView }
}

function Reveal({
  children,
  className = '',
  delay = 0,
  group = false,
}: {
  children: ReactNode
  className?: string
  delay?: number
  group?: boolean
}) {
  const { ref, inView } = useInView<HTMLDivElement>()
  return (
    <div
      ref={ref}
      className={`${group ? styles.group : styles.reveal} ${inView ? styles.in : ''} ${className}`.trim()}
      style={cssVars({ '--d': `${delay}ms` })}
    >
      {children}
    </div>
  )
}

function CountUp({
  from = 0,
  to,
  decimals = 0,
  active,
  duration = 1500,
}: {
  from?: number
  to: number
  decimals?: number
  active: boolean
  duration?: number
}) {
  const reduced = usePrefersReducedMotion()
  const [value, setValue] = useState(from)
  useEffect(() => {
    if (!active) return
    if (reduced) {
      setValue(to)
      return
    }
    let raf = 0
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setValue(from + (to - from) * eased)
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [active, from, to, duration, reduced])
  return (
    <>
      {value.toLocaleString('pt-PT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
    </>
  )
}

/* ---------------------------------------------------------------- dados */

const NAV_LINKS = [
  { href: '#funcionalidades', label: 'Funcionalidades' },
  { href: '#para-quem', label: 'Para quem é' },
  { href: '#precos', label: 'Preços' },
  { href: '#faq', label: 'FAQ' },
] as const

const ROTATING = [
  'salões',
  'clínicas',
  'consultórios',
  'barbearias',
  'ginásios',
  'oficinas',
  'explicadores',
  'estúdios',
  'todos',
] as const

const MARQUEE: Array<{ icon: IconName; label: string }> = [
  { icon: 'scissors', label: 'Salões e barbearias' },
  { icon: 'stethoscope', label: 'Clínicas e consultórios' },
  { icon: 'activity', label: 'Fisioterapia' },
  { icon: 'dumbbell', label: 'Ginásios e personal trainers' },
  { icon: 'sparkles', label: 'Estética e spa' },
  { icon: 'wrench', label: 'Oficinas e reparações' },
  { icon: 'grad', label: 'Aulas e explicações' },
  { icon: 'camera', label: 'Fotografia' },
  { icon: 'briefcase', label: 'Consultoria' },
  { icon: 'calendar', label: 'Qualquer serviço com hora marcada' },
]

const TEAM = [
  { name: 'Ana', color: '#0284c7' },
  { name: 'Rui', color: '#10b981' },
  { name: 'Eva', color: '#ec4899' },
] as const

type Tone = 'tBlue' | 'tViolet' | 'tMint' | 'tAmber' | 'tPink'

const AGENDA: Array<{ col: number; start: number; dur: number; name: string; who: string; tone: Tone }> = [
  { col: 0, start: 0, dur: 30, name: 'Corte', who: 'Inês', tone: 'tBlue' },
  { col: 0, start: 45, dur: 90, name: 'Coloração', who: 'Rita', tone: 'tViolet' },
  { col: 0, start: 165, dur: 45, name: 'Brushing', who: 'Joana', tone: 'tBlue' },
  { col: 1, start: 15, dur: 30, name: 'Barba', who: 'João', tone: 'tMint' },
  { col: 1, start: 60, dur: 60, name: 'Consulta', who: 'Pedro', tone: 'tAmber' },
  { col: 2, start: 0, dur: 60, name: 'Manicure', who: 'Sara', tone: 'tPink' },
  { col: 2, start: 90, dur: 60, name: 'Massagem', who: 'Luís', tone: 'tViolet' },
  { col: 2, start: 180, dur: 30, name: 'Sobrancelha', who: 'Eva', tone: 'tPink' },
]

function formatTime(minutesFromNine: number) {
  const total = 9 * 60 + minutesFromNine
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

type FeatureId = 'marcar' | 'lembretes' | 'painel' | 'app' | 'tarefas' | 'qr'

const FEATURES: Array<{ id: FeatureId; icon: IconName; title: string; text: string }> = [
  {
    id: 'marcar',
    icon: 'calendar',
    title: 'Marcação em 30 segundos',
    text: 'Serviço, profissional, dia e hora — com preço e duração reais. O cliente marca sem criar conta numa app de terceiros.',
  },
  {
    id: 'lembretes',
    icon: 'bell',
    title: 'Lembretes automáticos',
    text: 'E-mail 24 horas e 2 horas antes de cada marcação. Menos faltas e menos telefonemas.',
  },
  {
    id: 'painel',
    icon: 'dashboard',
    title: 'Painel do negócio',
    text: 'Agenda do dia, marcações por confirmar, faturação prevista e ocupação — num só ecrã.',
  },
  {
    id: 'app',
    icon: 'phone',
    title: 'App com a sua marca',
    text: 'No plano Completo, o seu negócio chega à App Store e ao Google Play com o seu nome e ícone.',
  },
  {
    id: 'tarefas',
    icon: 'tasks',
    title: 'Tarefas da equipa',
    text: 'Organize o dia a dia da equipa no mesmo sítio da agenda. Ninguém se esquece do que falta fazer.',
  },
  {
    id: 'qr',
    icon: 'qr',
    title: 'Link e QR code',
    text: 'Um endereço só seu para a montra, o Instagram e o Google. Quem lê o código, marca.',
  },
]

const SECTOR_TILES: Array<{ img: string; icon: IconName; title: string; chip: string; alt: string }> = [
  {
    img: IMG.salon,
    icon: 'sparkles',
    title: 'Beleza e bem-estar',
    chip: 'Coloração · 90 min · 45 €',
    alt: 'Recepção de um salão com a agenda num tablet',
  },
  {
    img: IMG.physio,
    icon: 'stethoscope',
    title: 'Saúde e clínicas',
    chip: 'Consulta · 45 min · 40 €',
    alt: 'Fisioterapeuta a tratar um paciente',
  },
  {
    img: IMG.barber,
    icon: 'scissors',
    title: 'Barbearias',
    chip: 'Barba · 20 min · 10 €',
    alt: 'Barbeiro a aparar a barba de um cliente',
  },
  {
    img: IMG.workshop,
    icon: 'wrench',
    title: 'Oficinas e reparações',
    chip: 'Revisão · 2 h · 89 €',
    alt: 'Mecânico com um tablet na oficina',
  },
  {
    img: IMG.tutor,
    icon: 'grad',
    title: 'Aulas e explicações',
    chip: 'Explicação · 60 min · 20 €',
    alt: 'Explicadora a ajudar uma aluna',
  },
  {
    img: IMG.gym,
    icon: 'dumbbell',
    title: 'Desporto e personal trainers',
    chip: 'Treino PT · 60 min · 25 €',
    alt: 'Personal trainer a orientar uma cliente',
  },
]

const COMPARE = [
  { them: 'O seu negócio aparece ao lado da concorrência', us: 'Um espaço só seu, com a sua marca' },
  { them: 'Planos com limites e extras pagos à parte', us: `Grátis para começar · Completo a ${MONTHLY_PRICE} €/mês` },
  { them: 'Cobra logo no dia em que entra', us: 'Só paga no dia 1 do mês seguinte' },
  { them: 'Sem ferramentas para a equipa', us: 'Tarefas da equipa no plano Completo' },
  { them: 'Uma app do marketplace', us: 'App GoBooking e app com a sua marca' },
]

const PLAN_FREE = [
  '1 unidade e 1 profissional',
  'Até 8 serviços com preço e duração',
  '40 marcações por mês',
  'Lembretes por e-mail',
  'Link do espaço e QR code',
  'Presença na app GoBooking',
]

const PLAN_FULL = [
  'Tudo do Grátis, sem limites',
  'Várias unidades e profissionais',
  'Tarefas da equipa no dia a dia',
  'App com o nome e o ícone da sua marca',
  'Suporte da Agência Sol OnLine',
  'Zero comissão por marcação',
]

const PROOF = [
  {
    quote:
      'Os clientes dizem “é o link do nosso salão” — não parece um serviço alugado. O Instagram aponta para nós e só para nós.',
    name: 'Gestora de salão',
    role: '2 unidades',
  },
  {
    quote:
      'Ter um espaço só nosso deu-nos credibilidade. Quem marca sente que está na nossa clínica, não numa app qualquer.',
    name: 'Clínica de estética',
    role: 'Equipa de 8',
  },
  {
    quote: 'Um link, uma marca, zero confusão. Parece que tínhamos app própria — sem o custo de desenvolver do zero.',
    name: 'Barbearia',
    role: 'Marca em crescimento',
  },
] as const

const FAQ = [
  {
    q: 'Para que negócios serve o GoBooking?',
    a: 'Para tudo e todos que trabalham com hora marcada: salões, clínicas, consultórios, barbearias, ginásios, oficinas, explicadores e qualquer outro serviço. Cada negócio configura os seus serviços, preços e durações.',
  },
  {
    q: 'É uma app partilhada com outros negócios?',
    a: 'Não. Cada empresa tem o seu ambiente isolado e o seu link (/e/nome-da-empresa). Os seus clientes só veem a sua marca, serviços e equipa — nunca um diretório com a concorrência ao lado.',
  },
  {
    q: 'Há comissão por marcação?',
    a: 'Não. As marcações são suas. Não ficamos com nenhuma percentagem sobre cada cliente que agenda.',
  },
  {
    q: 'Quanto custa?',
    a: `Há duas modalidades. Grátis para começar (1 unidade, 1 profissional, 8 serviços, 40 marcações por mês e lembretes). Completo a ${MONTHLY_PRICE} €/mês, sem taxa de setup: sem esses limites, com tarefas de equipa e app com a sua marca.`,
  },
  {
    q: 'Quando é que pago?',
    a: 'O plano Completo cobra-se sempre no dia 1, pelo mês civil (do dia 1 ao último dia do mês). Se entrar a meio do mês, não paga nada até ao dia 1 do mês seguinte — e usa o espaço na mesma.',
  },
  {
    q: 'Os clientes precisam de instalar alguma coisa?',
    a: 'Não. O cliente marca pelo link no browser. Quem preferir pode usar a app GoBooking — e, no plano Completo, a app com a marca do seu negócio.',
  },
  {
    q: 'Já sou cliente — onde entro?',
    a: 'Use o link que a sua empresa lhe enviou (formato /e/nome-da-empresa) ou abra a app GoBooking e escolha o espaço. Esta página é para quem quer o serviço.',
  },
] as const

/* QR decorativo: padrões de canto + células pseudo-aleatórias estáveis. */
const QR_SIZE = 21
const QR_CELLS: boolean[] = (() => {
  let seed = 11
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280
    return seed / 233280
  }
  const zones = [
    { r0: 0, c0: 0 },
    { r0: 0, c0: QR_SIZE - 7 },
    { r0: QR_SIZE - 7, c0: 0 },
  ]
  const finder = (r: number, c: number): boolean | null => {
    for (const z of zones) {
      const rr = r - z.r0
      const cc = c - z.c0
      if (rr >= -1 && rr <= 7 && cc >= -1 && cc <= 7) {
        if (rr < 0 || rr > 6 || cc < 0 || cc > 6) return false
        return Math.min(rr, cc, 6 - rr, 6 - cc) !== 1
      }
    }
    return null
  }
  const cells: boolean[] = []
  for (let r = 0; r < QR_SIZE; r++) {
    for (let c = 0; c < QR_SIZE; c++) {
      cells.push(finder(r, c) ?? rand() > 0.5)
    }
  }
  return cells
})()

function Qr({ className = '' }: { className?: string }) {
  return (
    <div className={`${styles.qr} ${className}`.trim()} aria-hidden>
      {QR_CELLS.map((on, i) => (
        <i key={i} className={on ? styles.qrOn : undefined} />
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------- hero */

function WordRotator() {
  const reduced = usePrefersReducedMotion()
  const [index, setIndex] = useState(0)
  useEffect(() => {
    if (reduced) return
    const id = window.setInterval(() => setIndex((n) => (n + 1) % ROTATING.length), 2200)
    return () => window.clearInterval(id)
  }, [reduced])
  const word = reduced ? ROTATING[ROTATING.length - 1] : ROTATING[index]
  return (
    <span className={styles.rotator} aria-hidden>
      <span key={word} className={styles.rotWord}>
        {word}
      </span>
    </span>
  )
}

function PhoneBooking() {
  const slots = ['10:00', '10:30', '11:30', '14:30', '15:00', '16:30']
  return (
    <div className={styles.phone}>
      <div className={styles.phoneScreen}>
        <div className={styles.phoneCover}>
          <img src={IMG.salon} alt="" />
        </div>
        <div className={styles.phoneBrand}>
          <span className={styles.phoneLogo}>SB</span>
          <div>
            <strong>Studio Bella</strong>
            <small>gobooking.pt/e/studio-bella</small>
          </div>
        </div>
        <p className={styles.phoneLabel}>Escolha o serviço</p>
        <ul className={styles.phoneServices}>
          <li className={styles.isSel}>
            <span>Corte</span>
            <em>30 min</em>
            <b>15 €</b>
          </li>
          <li>
            <span>Coloração</span>
            <em>90 min</em>
            <b>45 €</b>
          </li>
          <li>
            <span>Barba</span>
            <em>20 min</em>
            <b>10 €</b>
          </li>
        </ul>
        <p className={styles.phoneLabel}>Amanhã</p>
        <div className={styles.phoneSlots}>
          {slots.map((t) => (
            <span key={t} className={t === '14:30' ? styles.slotSel : t === '10:30' ? styles.slotOff : undefined}>
              {t}
            </span>
          ))}
        </div>
        <span className={styles.phoneCta}>Confirmar · 14:30</span>
      </div>
    </div>
  )
}

function HeroStage({ stageRef }: { stageRef: RefObject<HTMLDivElement> }) {
  return (
    <div ref={stageRef} className={styles.stage} aria-hidden>
      <div className={`${styles.layer} ${styles.lDash}`}>
        <div className={styles.dash}>
          <div className={styles.dashHead}>
            <div>
              <strong>Agenda de hoje</strong>
              <small>3 profissionais · 9 marcações</small>
            </div>
            <span className={styles.dashChip}>Ao vivo</span>
          </div>
          <div className={styles.dashCols}>
            <span />
            {TEAM.map((p) => (
              <span key={p.name} className={styles.dashColHead}>
                <i style={cssVars({ '--c': p.color })}>{p.name[0]}</i>
                {p.name}
              </span>
            ))}
          </div>
          <div className={styles.dashGrid}>
            <div className={styles.dashTimes}>
              {['09:00', '10:00', '11:00', '12:00', '13:00'].map((t, i) => (
                <span key={t} style={cssVars({ '--t': i * 60 })}>
                  {t}
                </span>
              ))}
            </div>
            {TEAM.map((p, col) => (
              <div key={p.name} className={styles.dashCol}>
                {AGENDA.filter((b) => b.col === col).map((b) => (
                  <div
                    key={`${b.name}-${b.start}`}
                    className={`${styles.block} ${styles[b.tone]} ${b.dur < 45 ? styles.short : ''}`}
                    style={cssVars({ '--s': b.start, '--d': b.dur })}
                  >
                    <strong>{b.name}</strong>
                    <span>{b.dur < 45 ? formatTime(b.start) : `${formatTime(b.start)} · ${b.who}`}</span>
                  </div>
                ))}
                {col === 1 ? (
                  <div className={`${styles.block} ${styles.blockNew}`} style={cssVars({ '--s': 150, '--d': 45 })}>
                    <strong>Nova marcação</strong>
                    <span>11:30 · Sofia</span>
                  </div>
                ) : null}
              </div>
            ))}
            <span className={styles.nowLine} style={cssVars({ '--t': 100 })} />
          </div>
        </div>
      </div>

      <div className={`${styles.layer} ${styles.lPhone}`}>
        <PhoneBooking />
      </div>

      <div className={`${styles.layer} ${styles.lToastA}`}>
        <div className={styles.toast}>
          <span className={`${styles.toastIcon} ${styles.toneMint}`}>
            <Icon name="check" />
          </span>
          <div>
            <strong>Marcação confirmada</strong>
            <small>Sofia · Corte · 14:30</small>
          </div>
        </div>
      </div>
      <div className={`${styles.layer} ${styles.lToastB}`}>
        <div className={styles.toast}>
          <span className={`${styles.toastIcon} ${styles.toneAmber}`}>
            <Icon name="bell" />
          </span>
          <div>
            <strong>Lembrete enviado</strong>
            <small>24 h antes, por e-mail</small>
          </div>
        </div>
      </div>
      <div className={`${styles.layer} ${styles.lToastC}`}>
        <div className={styles.toast}>
          <span className={`${styles.toastIcon} ${styles.toneBrand}`}>
            <Icon name="euro" />
          </span>
          <div>
            <strong>0% de comissão</strong>
            <small>A marcação é toda sua</small>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- passos */

function StepLinkVisual() {
  return (
    <div className={styles.stepLink}>
      <div className={styles.linkPill}>
        <Icon name="link" />
        <span className={styles.mono}>gobooking.pt/e/</span>
        <span className={`${styles.mono} ${styles.typed}`}>o-seu-negocio</span>
      </div>
      <ul className={styles.miniChecks}>
        {['Serviços', 'Horários', 'Equipa'].map((label, i) => (
          <li key={label} style={cssVars({ '--i': i })}>
            <Icon name="check" />
            {label}
          </li>
        ))}
      </ul>
    </div>
  )
}

function StepShareVisual() {
  return (
    <div className={styles.stepShare}>
      <div className={styles.qrSmallWrap}>
        <Qr className={styles.qrSmall} />
      </div>
      <span className={`${styles.shareChip} ${styles.sc1}`}>
        <Icon name="instagram" />
        Instagram
      </span>
      <span className={`${styles.shareChip} ${styles.sc2}`}>
        <WhatsAppIcon />
        WhatsApp
      </span>
      <span className={`${styles.shareChip} ${styles.sc3}`}>
        <Icon name="globe" />
        Google
      </span>
      <span className={`${styles.shareChip} ${styles.sc4}`}>
        <Icon name="store" />
        Montra
      </span>
    </div>
  )
}

function StepBookingsVisual() {
  const items = [
    { who: 'Sofia', what: 'Corte · 14:30', tone: 'toneMint' },
    { who: 'João', what: 'Revisão · 09:00', tone: 'toneBrand' },
    { who: 'Marta', what: 'Consulta · 11:15', tone: 'toneAmber' },
  ]
  return (
    <div className={styles.notifStack}>
      {items.map((n, i) => (
        <div key={n.who} className={styles.notif} style={cssVars({ '--i': i })}>
          <span className={`${styles.toastIcon} ${styles[n.tone]}`}>
            <Icon name="calendar" />
          </span>
          <div>
            <strong>Nova marcação</strong>
            <small>
              {n.who} · {n.what}
            </small>
          </div>
        </div>
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------- funcionalidades */

function VisualBooking() {
  const week = [
    ['Seg', '9'],
    ['Ter', '10'],
    ['Qua', '11'],
    ['Qui', '12'],
    ['Sex', '13'],
  ]
  const slots = ['09:30', '10:00', '11:30', '14:30', '15:00', '16:30']
  return (
    <div className={styles.vStack}>
      <ol className={`${styles.stepper} ${styles.pop}`}>
        <li className={styles.stDone}>
          <i>
            <Icon name="check" />
          </i>
          Serviço
        </li>
        <li className={styles.stDone}>
          <i>
            <Icon name="check" />
          </i>
          Profissional
        </li>
        <li className={styles.stCur}>
          <i>3</i>
          Dia e hora
        </li>
        <li>
          <i>4</i>
          Confirmar
        </li>
      </ol>
      <div className={styles.vGrid2}>
        <div className={`${styles.vCard} ${styles.pop}`} style={cssVars({ '--i': 1 })}>
          <p className={styles.vLabel}>Serviço</p>
          <ul className={styles.vList}>
            <li className={styles.vSel}>
              <span>
                <b>Corte</b>
                <small>30 min · com Ana</small>
              </span>
              <em>15 €</em>
            </li>
            <li>
              <span>
                <b>Coloração</b>
                <small>90 min</small>
              </span>
              <em>45 €</em>
            </li>
            <li>
              <span>
                <b>Barba</b>
                <small>20 min</small>
              </span>
              <em>10 €</em>
            </li>
          </ul>
        </div>
        <div className={`${styles.vCard} ${styles.pop}`} style={cssVars({ '--i': 2 })}>
          <p className={styles.vLabel}>Dia e hora</p>
          <div className={styles.vWeek}>
            {week.map(([d, n], i) => (
              <span key={d} className={i === 3 ? styles.wSel : undefined}>
                <small>{d}</small>
                <b>{n}</b>
              </span>
            ))}
          </div>
          <div className={styles.vSlots}>
            {slots.map((t) => (
              <span key={t} className={t === '14:30' ? styles.sSel : t === '10:00' ? styles.sOff : undefined}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className={`${styles.vConfirm} ${styles.pop}`} style={cssVars({ '--i': 4 })}>
        <span className={styles.okIcon}>
          <Icon name="check" />
        </span>
        <div>
          <strong>Marcação confirmada</strong>
          <small>Qui, 12 · 14:30 · Corte com a Ana · 15 €</small>
        </div>
      </div>
    </div>
  )
}

function VisualReminders() {
  return (
    <div className={styles.vGrid2}>
      <div className={`${styles.vMail} ${styles.pop}`} style={cssVars({ '--i': 1 })}>
        <div className={styles.mailHead}>
          <span className={styles.mailLogo}>SB</span>
          <div>
            <b>Studio Bella</b>
            <small>para sofia@email.pt</small>
          </div>
          <time>agora</time>
        </div>
        <p className={styles.mailSubject}>Lembrete: amanhã às 14:30</p>
        <p className={styles.mailBody}>
          Olá Sofia! A sua marcação de <b>Corte</b> com a Ana está confirmada para amanhã às <b>14:30</b>.
        </p>
        <div className={styles.mailBtns}>
          <span>Ver marcação</span>
          <span>Reagendar</span>
        </div>
      </div>
      <ol className={`${styles.vTimeline} ${styles.pop}`} style={cssVars({ '--i': 2 })}>
        <li className={styles.tlDone}>
          <i>
            <Icon name="check" />
          </i>
          <div>
            <b>Marcação criada</b>
            <small>Pelo link do espaço</small>
          </div>
        </li>
        <li className={styles.tlDone}>
          <i>
            <Icon name="check" />
          </i>
          <div>
            <b>24 horas antes</b>
            <small>Lembrete enviado por e-mail</small>
          </div>
        </li>
        <li className={styles.tlNow}>
          <i />
          <div>
            <b>2 horas antes</b>
            <small>A enviar o segundo lembrete</small>
          </div>
        </li>
        <li>
          <i />
          <div>
            <b>Cliente chega a horas</b>
            <small>Menos faltas, menos telefonemas</small>
          </div>
        </li>
      </ol>
    </div>
  )
}

function VisualDashboard() {
  const kpis = [
    { label: 'Marcações hoje', value: '12', note: '+3 que ontem' },
    { label: 'Por confirmar', value: '2', note: 'Responder agora' },
    { label: 'Faturação prevista', value: '340 €', note: 'Hoje' },
    { label: 'Ocupação', value: '82 %', note: 'Esta semana' },
  ]
  const bars = [38, 52, 44, 70, 86, 64, 30]
  const days = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D']
  const next = [
    { t: '14:30', who: 'Sofia', what: 'Corte · Ana', c: '#0284c7' },
    { t: '15:00', who: 'João', what: 'Barba · Rui', c: '#10b981' },
    { t: '15:30', who: 'Marta', what: 'Manicure · Eva', c: '#ec4899' },
  ]
  return (
    <div className={styles.vStack}>
      <div className={styles.kpis}>
        {kpis.map((k, i) => (
          <div key={k.label} className={`${styles.kpi} ${styles.pop}`} style={cssVars({ '--i': i })}>
            <small>{k.label}</small>
            <b>{k.value}</b>
            <span>{k.note}</span>
          </div>
        ))}
      </div>
      <div className={styles.vGrid2}>
        <div className={`${styles.vCard} ${styles.pop}`} style={cssVars({ '--i': 4 })}>
          <p className={styles.vLabel}>Marcações esta semana</p>
          <div className={styles.bars}>
            {bars.map((h, i) => (
              <span key={i} style={cssVars({ '--h': `${h}%`, '--i': i })}>
                <i />
                <small>{days[i]}</small>
              </span>
            ))}
          </div>
        </div>
        <div className={`${styles.vCard} ${styles.pop}`} style={cssVars({ '--i': 5 })}>
          <p className={styles.vLabel}>A seguir</p>
          <ul className={styles.nextList}>
            {next.map((n) => (
              <li key={n.t}>
                <time>{n.t}</time>
                <i style={cssVars({ '--c': n.c })} />
                <span>
                  <b>{n.who}</b>
                  <small>{n.what}</small>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function VisualApp() {
  return (
    <div className={styles.vApp}>
      <div className={`${styles.store} ${styles.pop}`}>
        <span className={styles.storeIcon}>SB</span>
        <div className={styles.storeMeta}>
          <b>Studio Bella</b>
          <small>Marcações · Beleza</small>
          <span className={styles.storeRating}>
            <Stars className={styles.stars} />
            <em>4,9</em>
          </span>
        </div>
        <span className={styles.getBtn}>Obter</span>
      </div>
      <div className={styles.shots}>
        <div className={`${styles.shot} ${styles.pop}`} style={cssVars({ '--i': 1 })}>
          <span className={styles.shotCover} />
          <span className={styles.shotLine} />
          <span className={`${styles.shotLine} ${styles.shotShort}`} />
          <span className={styles.shotRow} />
          <span className={styles.shotRow} />
          <span className={styles.shotRow} />
        </div>
        <div className={`${styles.shot} ${styles.shotMid} ${styles.pop}`} style={cssVars({ '--i': 2 })}>
          <span className={styles.shotLine} />
          <div className={styles.shotGrid}>
            {Array.from({ length: 12 }, (_, i) => (
              <i key={i} className={i === 7 ? styles.shotOn : undefined} />
            ))}
          </div>
          <span className={styles.shotBtn} />
        </div>
        <div className={`${styles.shot} ${styles.pop}`} style={cssVars({ '--i': 3 })}>
          <span className={styles.shotCheck}>
            <Icon name="check" />
          </span>
          <span className={styles.shotLine} />
          <span className={`${styles.shotLine} ${styles.shotShort}`} />
          <span className={styles.shotBtn} />
        </div>
      </div>
      <div className={`${styles.badges} ${styles.pop}`} style={cssVars({ '--i': 4 })}>
        <span>Disponível na App Store</span>
        <span>Disponível no Google Play</span>
      </div>
    </div>
  )
}

function VisualTasks() {
  const cols = [
    {
      title: 'Por fazer',
      items: [
        { tag: 'Stock', c: '#f59e0b', title: 'Encomendar produtos', who: 'A', when: 'Hoje' },
        { tag: 'Clientes', c: '#0284c7', title: 'Ligar à Sofia', who: 'R', when: '11:00' },
      ],
    },
    {
      title: 'Em curso',
      items: [{ tag: 'Espaço', c: '#7c3aed', title: 'Preparar a sala 2', who: 'E', when: 'Agora' }],
    },
    {
      title: 'Feito',
      items: [
        { tag: 'Rotina', c: '#10b981', title: 'Abrir a caixa', who: 'A', when: '09:00', done: true },
        { tag: 'Agenda', c: '#10b981', title: 'Confirmar marcações de amanhã', who: 'R', when: '09:20', done: true },
      ],
    },
  ]
  let n = 0
  return (
    <div className={styles.kanban}>
      {cols.map((col) => (
        <div key={col.title} className={styles.kCol}>
          <p className={styles.kHead}>
            {col.title}
            <span>{col.items.length}</span>
          </p>
          {col.items.map((t) => (
            <div
              key={t.title}
              className={`${styles.kCard} ${'done' in t && t.done ? styles.kDone : ''} ${styles.pop}`}
              style={cssVars({ '--i': n++ })}
            >
              <span className={styles.kTag} style={cssVars({ '--c': t.c })}>
                {t.tag}
              </span>
              <b>{t.title}</b>
              <div className={styles.kMeta}>
                <i>{t.who}</i>
                <small>{t.when}</small>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

function VisualQr() {
  return (
    <div className={styles.vQr}>
      <div className={`${styles.qrCard} ${styles.pop}`}>
        <span className={styles.qrTitle}>Aponte a câmara para marcar</span>
        <Qr className={styles.qrBig} />
        <b>Studio Bella</b>
        <small>gobooking.pt/e/studio-bella</small>
      </div>
      <div className={styles.vStack}>
        <div className={`${styles.linkPill} ${styles.linkPillWide} ${styles.pop}`} style={cssVars({ '--i': 1 })}>
          <Icon name="link" />
          <span>gobooking.pt/e/studio-bella</span>
          <em className={styles.copied}>Copiado</em>
        </div>
        <div className={styles.shareRow}>
          {[
            { icon: <Icon name="instagram" />, label: 'Instagram' },
            { icon: <WhatsAppIcon />, label: 'WhatsApp' },
            { icon: <Icon name="globe" />, label: 'Google' },
            { icon: <Icon name="store" />, label: 'Montra' },
          ].map((s, i) => (
            <span key={s.label} className={styles.pop} style={cssVars({ '--i': i + 2 })}>
              {s.icon}
              {s.label}
            </span>
          ))}
        </div>
        <p className={`${styles.vNote} ${styles.pop}`} style={cssVars({ '--i': 6 })}>
          Imprima o código para a montra, o balcão ou o espelho. Cada leitura abre o seu espaço, pronto a marcar.
        </p>
      </div>
    </div>
  )
}

const VISUALS: Record<FeatureId, () => JSX.Element> = {
  marcar: VisualBooking,
  lembretes: VisualReminders,
  painel: VisualDashboard,
  app: VisualApp,
  tarefas: VisualTasks,
  qr: VisualQr,
}

function FeatureShowcase() {
  const reduced = usePrefersReducedMotion()
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  const { ref, inView } = useInView<HTMLDivElement>(false, 0.25)
  const listRef = useRef<HTMLDivElement | null>(null)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const running = inView && !paused && !reduced
  const feature = FEATURES[active]
  const ActiveVisual = VISUALS[feature.id]

  useEffect(() => {
    const list = listRef.current
    const tab = tabRefs.current[active]
    if (!list || !tab || list.scrollWidth <= list.clientWidth) return
    list.scrollTo({ left: tab.offsetLeft - 16, behavior: 'smooth' })
  }, [active])

  return (
    <div
      ref={ref}
      className={styles.showcase}
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') setPaused(true)
      }}
      onPointerLeave={() => setPaused(false)}
    >
      <div>
        <div ref={listRef} className={styles.tabList} role="tablist" aria-label="Funcionalidades do GoBooking">
          {FEATURES.map((f, i) => (
            <button
              key={f.id}
              ref={(el) => {
                tabRefs.current[i] = el
              }}
              type="button"
              role="tab"
              id={`tab-${f.id}`}
              aria-selected={i === active}
              aria-controls="feature-panel"
              className={`${styles.tab} ${i === active ? styles.tabOn : ''}`}
              onClick={() => setActive(i)}
            >
              <span className={styles.tabIcon}>
                <Icon name={f.icon} />
              </span>
              <span className={styles.tabText}>
                <strong>{f.title}</strong>
                <span>{f.text}</span>
              </span>
              {i === active ? (
                <span
                  key={active}
                  className={styles.tabProgress}
                  style={{ animationPlayState: running ? 'running' : 'paused' }}
                  onAnimationEnd={() => setActive((n) => (n + 1) % FEATURES.length)}
                />
              ) : null}
            </button>
          ))}
        </div>
        <p className={styles.tabMobileText}>{feature.text}</p>
      </div>
      <div className={styles.showcaseFrame}>
        <div className={styles.frameBar} aria-hidden>
          <i />
          <i />
          <i />
          <span>gobooking.pt/e/studio-bella</span>
        </div>
        <div key={feature.id} id="feature-panel" role="tabpanel" aria-labelledby={`tab-${feature.id}`} className={styles.frameBody}>
          <ActiveVisual />
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- secções */

function SectionHead({
  kicker,
  title,
  lead,
  center = false,
  dark = false,
}: {
  kicker: string
  title: ReactNode
  lead?: ReactNode
  center?: boolean
  dark?: boolean
}) {
  return (
    <Reveal className={`${styles.head} ${center ? styles.headCenter : ''} ${dark ? styles.headDark : ''}`}>
      <span className={styles.kicker}>{kicker}</span>
      <h2 className={styles.h2}>{title}</h2>
      {lead ? <p className={styles.lead}>{lead}</p> : null}
    </Reveal>
  )
}

function StatsBand() {
  const { ref, inView } = useInView<HTMLDivElement>(true, 0.3)
  return (
    <section className={styles.statsWrap} aria-label="GoBooking em números">
      <div className={styles.wrap}>
        <div ref={ref} className={`${styles.stats} ${inView ? styles.in : ''}`}>
          <div className={styles.stat}>
            <span className={styles.statNum}>
              <CountUp from={637} to={0} active={inView} /> €
            </span>
            <span className={styles.statLabel}>
              de setup <s>637 €</s>
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statNum}>
              <CountUp to={MONTHLY_NUMBER} decimals={2} active={inView} /> €
            </span>
            <span className={styles.statLabel}>por mês, plano Completo</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statNum}>0 %</span>
            <span className={styles.statLabel}>de comissão por marcação</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statNum}>
              <CountUp to={30} active={inView} /> s
            </span>
            <span className={styles.statLabel}>para o cliente marcar</span>
          </div>
        </div>
      </div>
    </section>
  )
}

function Faq() {
  const [open, setOpen] = useState<number | null>(0)
  return (
    <div className={styles.faqList}>
      {FAQ.map((item, i) => {
        const isOpen = open === i
        return (
          <div key={item.q} className={`${styles.faqItem} ${isOpen ? styles.faqOpen : ''}`}>
            <button
              type="button"
              className={styles.faqQ}
              aria-expanded={isOpen}
              aria-controls={`faq-${i}`}
              onClick={() => setOpen(isOpen ? null : i)}
            >
              <span>{item.q}</span>
              <span className={styles.faqIcon}>
                <Icon name="plus" />
              </span>
            </button>
            <div id={`faq-${i}`} className={styles.faqA}>
              <div>
                <p>{item.a}</p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ---------------------------------------------------------------- página */

export function AppNoTenantLanding() {
  const reduced = usePrefersReducedMotion()
  const [scrolled, setScrolled] = useState(false)
  const [showFab, setShowFab] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const heroRef = useRef<HTMLElement | null>(null)
  const stageRef = useRef<HTMLDivElement | null>(null)
  const progressRef = useRef<HTMLSpanElement | null>(null)
  const { ref: billingRef, inView: billingIn } = useInView<HTMLDivElement>(true, 0.4)

  useEffect(() => {
    applyMarketingLandingDocumentTheme()
    document.title = 'GoBooking — Marcações sempre em dia, para tudo e todos | Agência Sol OnLine'
    const desc = `GoBooking é para tudo e todos que queiram ter as marcações em dia: link próprio, app com a sua marca, duração real e lembretes. Grátis para começar · Completo a ${MONTHLY_PRICE} €/mês.`
    let meta = document.querySelector('meta[name="description"]')
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('name', 'description')
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', desc)
  }, [])

  useEffect(() => {
    let raf = 0
    const update = () => {
      raf = 0
      const y = window.scrollY
      const max = document.documentElement.scrollHeight - window.innerHeight
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`
      }
      setScrolled(y > 24)
      setShowFab(y > 640)
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  useEffect(() => {
    const hero = heroRef.current
    const stage = stageRef.current
    if (!hero || !stage || reduced) return
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return
    let raf = 0
    const onMove = (e: PointerEvent) => {
      const rect = hero.getBoundingClientRect()
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        stage.style.setProperty('--mx', x.toFixed(3))
        stage.style.setProperty('--my', y.toFixed(3))
      })
    }
    const onLeave = () => {
      stage.style.setProperty('--mx', '0')
      stage.style.setProperty('--my', '0')
    }
    hero.addEventListener('pointermove', onMove)
    hero.addEventListener('pointerleave', onLeave)
    return () => {
      cancelAnimationFrame(raf)
      hero.removeEventListener('pointermove', onMove)
      hero.removeEventListener('pointerleave', onLeave)
    }
  }, [reduced])

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  return (
    <div className={styles.root}>
      <header className={`${styles.nav} ${scrolled || menuOpen ? styles.navSolid : ''}`}>
        <div className={`${styles.wrap} ${styles.navInner}`}>
          <a href="#top" className={styles.brand} aria-label="GoBooking — início">
            <img src="/brand/logo-horizontal-white.svg" alt="GoBooking" width={130} height={32} />
          </a>
          <nav className={styles.navLinks} aria-label="Secções">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href}>
                {l.label}
              </a>
            ))}
            <Link to="/app">App clientes</Link>
          </nav>
          <div className={styles.navActions}>
            <a className={styles.navCta} href={WA_ESPACO} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon />
              <span className={styles.navCtaText}>Quero o meu espaço</span>
            </a>
            <button
              type="button"
              className={styles.navToggle}
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <Icon name={menuOpen ? 'x' : 'menu'} />
            </button>
          </div>
        </div>
        {menuOpen ? (
          <div className={styles.mobileMenu}>
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}>
                {l.label}
              </a>
            ))}
            <Link to="/app" onClick={() => setMenuOpen(false)}>
              App clientes
            </Link>
          </div>
        ) : null}
        <span ref={progressRef} className={styles.progress} aria-hidden />
      </header>

      <main>
        <section id="top" ref={heroRef} className={styles.hero}>
          <div className={styles.heroCanvas} aria-hidden>
            <img className={styles.hp1} src={IMG.barber} alt="" />
            <span className={styles.heroShade} />
            <span className={styles.heroGrain} />
          </div>

          <div className={`${styles.wrap} ${styles.heroInner}`}>
            <div className={styles.heroCopy}>
              <a className={styles.heroBadge} href="#precos">
                <b>Novo</b>
                Comece grátis · Completo a {MONTHLY_PRICE} €
                <Icon name="arrow" />
              </a>
              <h1 className={styles.heroTitle}>
                As suas marcações, <em>sempre em dia.</em>
              </h1>
              <p className={styles.heroFor}>
                Feito para <WordRotator />
                <span className={styles.srOnly}>salões, clínicas, consultórios, oficinas e qualquer negócio</span>
              </p>
              <p className={styles.heroLead}>
                O GoBooking é para tudo e todos que vivem de hora marcada. O seu link, a sua marca e uma agenda que
                se organiza sozinha — com lembretes automáticos e marcação em 30 segundos.
              </p>
              <div className={styles.heroCtas}>
                <a className={styles.ctaMain} href={WA_ESPACO} target="_blank" rel="noopener noreferrer">
                  <WhatsAppIcon />
                  Quero o meu espaço
                  <Icon name="arrow" className={styles.arrow} />
                </a>
                <a className={styles.ctaGlass} href="#como">
                  Ver como funciona
                </a>
              </div>
              <ul className={styles.heroTrust}>
                <li>
                  <Icon name="check" />
                  Sem taxa de setup
                </li>
                <li>
                  <Icon name="check" />
                  0% de comissão
                </li>
                <li>
                  <Icon name="check" />
                  Completo a {MONTHLY_PRICE} €/mês
                </li>
              </ul>
            </div>
            <div className={styles.heroArt}>
              <img className={styles.hp2} src={IMG.salon} alt="" />
              <img className={styles.hp3} src={IMG.clinic} alt="" />
              <HeroStage stageRef={stageRef} />
              <div className={styles.priceTag}>
                <small>Plano Completo</small>
                <b>
                  {MONTHLY_PRICE} <span>€</span>
                </b>
                <em>/ mês · sem setup</em>
              </div>
            </div>
          </div>

          <div className={styles.marquee}>
            <div className={styles.marqueeTrack}>
              {[...MARQUEE, ...MARQUEE].map((s, i) => (
                <span key={`${s.label}-${i}`} className={styles.marqueeItem} aria-hidden={i >= MARQUEE.length}>
                  <Icon name={s.icon} />
                  {s.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        <StatsBand />

        <section id="como" className={styles.section}>
          <div className={styles.wrap}>
            <SectionHead
              kicker="Como funciona"
              title={
                <>
                  Do primeiro olá à agenda cheia. <em>Em três tempos.</em>
                </>
              }
              lead="Sem instalações complicadas nem formações longas. Tratamos da configuração consigo e o seu espaço fica pronto a receber marcações."
            />
            <Reveal group className={styles.story}>
              <article className={styles.storyRow} style={cssVars({ '--i': 0 })}>
                <div className={styles.storyVisual}>
                  <StepLinkVisual />
                </div>
                <div className={styles.storyCopy}>
                  <span className={styles.storyNum}>01</span>
                  <h3>Criamos o seu espaço</h3>
                  <p>
                    Fale connosco no WhatsApp. Deixamos o link, os serviços, os preços e os horários prontos — com a
                    sua marca.
                  </p>
                </div>
              </article>
              <article className={`${styles.storyRow} ${styles.storyRev}`} style={cssVars({ '--i': 1 })}>
                <div className={styles.storyVisual}>
                  <StepShareVisual />
                </div>
                <div className={styles.storyCopy}>
                  <span className={styles.storyNum}>02</span>
                  <h3>Partilhe o link e o QR</h3>
                  <p>Na montra, no Instagram, no Google ou numa mensagem. Quem abre, marca — no browser ou na app.</p>
                </div>
              </article>
              <article className={styles.storyRow} style={cssVars({ '--i': 2 })}>
                <div className={styles.storyVisual}>
                  <StepBookingsVisual />
                </div>
                <div className={styles.storyCopy}>
                  <span className={styles.storyNum}>03</span>
                  <h3>Receba marcações</h3>
                  <p>
                    Tudo entra na agenda certa, com a duração real de cada serviço. O cliente recebe lembretes e a
                    equipa sabe o que vem a seguir.
                  </p>
                </div>
              </article>
            </Reveal>
          </div>
        </section>

        <section id="funcionalidades" className={`${styles.section} ${styles.sectionWhite}`}>
          <div className={styles.wrap}>
            <SectionHead
              kicker="Funcionalidades"
              title={
                <>
                  Tudo o que precisa para gerir marcações. <em>Num só sítio.</em>
                </>
              }
              lead="Do lado do cliente, marcar é rápido. Do seu lado, a agenda, os lembretes e a equipa ficam organizados."
            />
            <Reveal>
              <FeatureShowcase />
            </Reveal>
          </div>
        </section>

        <section id="para-quem" className={`${styles.section} ${styles.sectionFilm}`}>
          <div className={styles.wrap}>
            <SectionHead
              kicker="Para tudo e todos"
              title={
                <>
                  Se trabalha com hora marcada, <em>o GoBooking é para si.</em>
                </>
              }
              lead="Beleza, saúde, desporto, oficinas, aulas ou outro serviço qualquer. Cada negócio tem o seu espaço, com os seus serviços, preços e durações."
            />
          </div>
          <Reveal group className={styles.film}>
            {SECTOR_TILES.map((t, i) => (
              <article key={t.title} className={styles.tile} style={cssVars({ '--i': i })}>
                <img src={t.img} alt={t.alt} loading="lazy" decoding="async" />
                <span className={styles.tileChip}>
                  <Icon name="check" />
                  {t.chip}
                </span>
                <div className={styles.tileBody}>
                  <span className={styles.tileIcon}>
                    <Icon name={t.icon} />
                  </span>
                  <h3>{t.title}</h3>
                </div>
              </article>
            ))}
          </Reveal>
        </section>

        <section id="porque" className={`${styles.section} ${styles.sectionWhite}`}>
          <div className={styles.wrap}>
            <SectionHead
              kicker="Porquê o GoBooking"
              title={
                <>
                  Melhor do que um diretório partilhado. <em>E com preço claro.</em>
                </>
              }
              lead="Não colocamos o seu negócio ao lado da concorrência. O espaço é seu, as marcações são suas e sabe sempre quanto paga."
            />
            <Reveal group className={styles.bento}>
              <article className={styles.bBrand} style={cssVars({ '--i': 0 })}>
                <img src={IMG.clinic} alt="Sala de espera de uma clínica com imagem própria" loading="lazy" decoding="async" />
                <div className={styles.bBrandBody}>
                  <span className={styles.bKicker}>A sua marca</span>
                  <h3 className={styles.bTitle}>O cliente entra no seu espaço — nunca numa lista de concorrentes.</h3>
                  <p className={styles.bText}>
                    O seu nome, as suas cores, os seus serviços e a sua equipa. É o canal digital do seu negócio.
                  </p>
                  <span className={styles.urlBar}>
                    <Icon name="shield" />
                    gobooking.pt/e/<b>clinica-aurora</b>
                  </span>
                </div>
              </article>
              <article className={`${styles.bTile} ${styles.bDark}`} style={cssVars({ '--i': 1 })}>
                <span className={styles.bBig}>0%</span>
                <h3 className={styles.bTitle}>de comissão</h3>
                <p className={styles.bText}>Cada marcação é sua. Não ficamos com nenhuma percentagem dos seus clientes.</p>
              </article>
              <article className={`${styles.bTile} ${styles.bGrad}`} style={cssVars({ '--i': 2 })}>
                <span className={styles.bStrike}>637 €</span>
                <span className={styles.bBig}>0 €</span>
                <h3 className={styles.bTitle}>de setup</h3>
                <p className={styles.bText}>Começa sem investimento inicial. Paga só a mensalidade, se escolher o Completo.</p>
              </article>
              <article className={styles.bTile} style={cssVars({ '--i': 3 })}>
                <span className={styles.bIcon}>
                  <Icon name="clock" />
                </span>
                <h3 className={styles.bTitle}>Duração real de cada serviço</h3>
                <p className={styles.bText}>Um corte de 30 minutos e uma coloração de 90 não ocupam o mesmo espaço.</p>
                <div className={styles.durRows}>
                  {[
                    { label: 'Corte', w: '33%', c: '#0284c7', t: '30 min' },
                    { label: 'Consulta', w: '50%', c: '#f59e0b', t: '45 min' },
                    { label: 'Coloração', w: '100%', c: '#7c3aed', t: '90 min' },
                  ].map((r, i) => (
                    <div key={r.label} className={styles.durRow} style={cssVars({ '--w': r.w, '--c': r.c, '--i': i })}>
                      <span>{r.label}</span>
                      <i>
                        <em>{r.t}</em>
                      </i>
                    </div>
                  ))}
                </div>
              </article>
              <article className={styles.bTile} style={cssVars({ '--i': 4 })}>
                <span className={styles.bIcon}>
                  <Icon name="users" />
                </span>
                <h3 className={styles.bTitle}>Várias unidades e profissionais</h3>
                <p className={styles.bText}>Horários por pessoa e por unidade, tudo na mesma marca.</p>
                <div className={styles.avatars} aria-hidden>
                  {[
                    { l: 'A', c: '#0284c7' },
                    { l: 'R', c: '#10b981' },
                    { l: 'E', c: '#ec4899' },
                    { l: 'M', c: '#7c3aed' },
                  ].map((a) => (
                    <span key={a.l} style={cssVars({ '--c': a.c })}>
                      {a.l}
                    </span>
                  ))}
                  <span className={styles.avatarMore}>+5</span>
                </div>
                <div className={styles.unitChips}>
                  <span>Lisboa</span>
                  <span>Porto</span>
                  <span>Braga</span>
                </div>
              </article>
              <article className={`${styles.bTile} ${styles.bCompare}`} style={cssVars({ '--i': 5 })}>
                <div className={styles.cmpCol}>
                  <h3>Diretório partilhado</h3>
                  <ul>
                    {COMPARE.map((row) => (
                      <li key={row.them}>
                        <span className={styles.cmpX}>
                          <Icon name="x" />
                        </span>
                        {row.them}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`${styles.cmpCol} ${styles.cmpUs}`}>
                  <h3>
                    <img src="/brand/mark.svg" alt="" width={24} height={24} />
                    GoBooking
                  </h3>
                  <ul>
                    {COMPARE.map((row) => (
                      <li key={row.us}>
                        <span className={styles.cmpOk}>
                          <Icon name="check" />
                        </span>
                        {row.us}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          </div>
        </section>

        <section id="precos" className={styles.pricing}>
          <div className={styles.pricingGlow} aria-hidden />
          <div className={styles.wrap}>
            <SectionHead
              center
              dark
              kicker="Preços"
              title={
                <>
                  Comece grátis. <em>Cresça quando quiser.</em>
                </>
              }
              lead="Sem taxa de setup e sem comissão por marcação. O plano Completo cobra-se no dia 1 de cada mês."
            />
            <Reveal group className={styles.plans}>
              <article className={styles.plan} style={cssVars({ '--i': 0 })}>
                <h3 className={styles.planName}>Grátis</h3>
                <p className={styles.planDesc}>Para começar a receber marcações hoje.</p>
                <p className={styles.planPrice}>
                  <b>0 €</b>
                  <span>para sempre</span>
                </p>
                <p className={styles.planNote}>Sem cartão · Sem prazo de teste a expirar</p>
                <ul className={styles.planList}>
                  {PLAN_FREE.map((f) => (
                    <li key={f}>
                      <Icon name="check" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a className={`${styles.ctaGlass} ${styles.ctaBlock}`} href={WA_ESPACO} target="_blank" rel="noopener noreferrer">
                  Começar grátis
                </a>
              </article>
              <article className={`${styles.plan} ${styles.planFeatured}`} style={cssVars({ '--i': 1 })}>
                <span className={styles.planBadge}>Recomendado</span>
                <h3 className={styles.planName}>Completo</h3>
                <p className={styles.planDesc}>Acesso a tudo, para negócios que querem crescer.</p>
                <p className={styles.planPrice}>
                  <b>{MONTHLY_PRICE} €</b>
                  <span>/ mês</span>
                </p>
                <p className={styles.planNote}>Menos de {DAILY_COMPARE} € por dia · IVA não incluído</p>
                <ul className={styles.planList}>
                  {PLAN_FULL.map((f) => (
                    <li key={f}>
                      <Icon name="check" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a className={`${styles.ctaMain} ${styles.ctaBlock}`} href={WA_ESPACO} target="_blank" rel="noopener noreferrer">
                  <WhatsAppIcon />
                  Quero o Completo
                  <Icon name="arrow" className={styles.arrow} />
                </a>
              </article>
            </Reveal>

            <div ref={billingRef} className={`${styles.billing} ${billingIn ? styles.in : ''}`}>
              <p className={styles.billingTitle}>
                <Icon name="calendar" />
                Como funciona a cobrança do Completo
              </p>
              <ol className={styles.billingSteps}>
                <li>
                  <b>Hoje</b>
                  <span>Cria o espaço e começa a usar</span>
                </li>
                <li>
                  <b>Até ao fim do mês</b>
                  <span>Não paga nada</span>
                </li>
                <li>
                  <b>Dia 1</b>
                  <span>Primeira mensalidade de {MONTHLY_PRICE} €</span>
                </li>
                <li>
                  <b>Todos os meses</b>
                  <span>Sempre no dia 1, do dia 1 ao último dia do mês</span>
                </li>
              </ol>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.wrap}>
            <SectionHead
              kicker="Depoimentos"
              title={
                <>
                  Quem tem espaço próprio <em>sente a diferença.</em>
                </>
              }
              lead="Negócios que passaram do “manda mensagem” para um link com a sua marca."
            />
            <Reveal group className={styles.quoteBoard}>
              <figure className={styles.quoteMain} style={cssVars({ '--i': 0 })}>
                <Stars className={styles.quoteStars} />
                <blockquote className={styles.quoteText}>{PROOF[0].quote}</blockquote>
                <figcaption className={styles.quoteWho}>
                  <i aria-hidden>{PROOF[0].name[0]}</i>
                  <span>
                    <b>{PROOF[0].name}</b>
                    <small>{PROOF[0].role}</small>
                  </span>
                </figcaption>
              </figure>
              <div className={styles.quoteSide}>
                {PROOF.slice(1).map((p, i) => (
                  <figure key={p.name} className={styles.quote} style={cssVars({ '--i': i + 1 })}>
                    <Stars className={styles.quoteStars} />
                    <blockquote className={styles.quoteText}>{p.quote}</blockquote>
                    <figcaption className={styles.quoteWho}>
                      <i aria-hidden>{p.name[0]}</i>
                      <span>
                        <b>{p.name}</b>
                        <small>{p.role}</small>
                      </span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section id="faq" className={`${styles.section} ${styles.sectionWhite}`}>
          <div className={`${styles.wrap} ${styles.faqGrid}`}>
            <div className={styles.faqAside}>
              <SectionHead kicker="Dúvidas" title="Perguntas frequentes" />
              <Reveal className={styles.faqHelp}>
                <b>Não encontra a resposta?</b>
                <p>Fale connosco no WhatsApp ou por e-mail. Respondemos com gosto.</p>
                <a className={styles.ctaMain} href={WA_DUVIDA} target="_blank" rel="noopener noreferrer">
                  <WhatsAppIcon />
                  Falar no WhatsApp
                </a>
                <a className={styles.faqMail} href={MAIL_CONTACT}>
                  <Icon name="mail" />
                  {CONTACT_EMAIL}
                </a>
              </Reveal>
            </div>
            <Reveal>
              <Faq />
            </Reveal>
          </div>
        </section>

        <section className={styles.final}>
          <div className={styles.wrap}>
            <Reveal className={styles.finalCard}>
              <span className={styles.finalOrbA} aria-hidden />
              <span className={styles.finalOrbB} aria-hidden />
              <h2 className={styles.finalTitle}>Pronto para ter as marcações sempre em dia?</h2>
              <p className={styles.finalLead}>
                Fale connosco no WhatsApp. Criamos o seu espaço e mostramos como fica — sem compromisso e sem taxa de
                setup.
              </p>
              <div className={styles.finalCtas}>
                <a className={styles.ctaWhite} href={WA_ESPACO} target="_blank" rel="noopener noreferrer">
                  <WhatsAppIcon />
                  Quero o meu espaço
                </a>
                <a className={styles.ctaGhostW} href={WA_APP} target="_blank" rel="noopener noreferrer">
                  Quero a app
                </a>
              </div>
              <ul className={styles.finalTrust}>
                <li>
                  <Icon name="check" />
                  Plano grátis para começar
                </li>
                <li>
                  <Icon name="check" />
                  0% de comissão
                </li>
                <li>
                  <Icon name="check" />
                  Pagamento só no dia 1
                </li>
              </ul>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.wrap}>
          <div className={styles.footTop}>
            <div className={styles.footBrand}>
              <img src="/brand/logo-horizontal-white.svg" alt="GoBooking" width={130} height={32} loading="lazy" />
              <p>Marcações sempre em dia, para tudo e todos. Uma solução da Agência Sol OnLine.</p>
            </div>
            <div className={styles.footCol}>
              <h4>Produto</h4>
              <ul>
                <li>
                  <a href="#como">Como funciona</a>
                </li>
                <li>
                  <a href="#funcionalidades">Funcionalidades</a>
                </li>
                <li>
                  <a href="#para-quem">Para quem é</a>
                </li>
                <li>
                  <a href="#precos">Preços</a>
                </li>
                <li>
                  <Link to="/app">App clientes</Link>
                </li>
              </ul>
            </div>
            <div className={styles.footCol}>
              <h4>Contacto</h4>
              <ul>
                <li>
                  <a href={WA_DUVIDA} target="_blank" rel="noopener noreferrer">
                    <WhatsAppIcon />
                    {AGENCY_WHATSAPP_LABEL}
                  </a>
                </li>
                <li>
                  <a href={MAIL_CONTACT}>
                    <Icon name="mail" />
                    {CONTACT_EMAIL}
                  </a>
                </li>
                <li>
                  <a href={SOLONLINE_SITE_URL} target="_blank" rel="noopener noreferrer">
                    <Icon name="globe" />
                    solonline.pt
                  </a>
                </li>
              </ul>
            </div>
            <div className={styles.footCol}>
              <h4>Legal</h4>
              <ul>
                <li>
                  <Link to="/privacidade">Política de Privacidade</Link>
                </li>
                <li>
                  <Link to="/termos">Termos de Utilização</Link>
                </li>
                <li>
                  <Link to="/eliminar-conta">Eliminar conta</Link>
                </li>
              </ul>
            </div>
          </div>
          <p className={styles.footHint}>
            <b>Já é cliente?</b> Entre pelo link que a empresa lhe enviou — por exemplo{' '}
            <code>gobooking.pt/e/nome-da-empresa</code> — ou pela <Link to="/app">App GoBooking</Link>.
          </p>
          <div className={styles.footBottom}>
            <p>
              © 2026 –{' '}
              <a href={SOLONLINE_SITE_URL} target="_blank" rel="noopener noreferrer">
                Agência Sol OnLine
              </a>
            </p>
            <p>Marcações sempre em dia.</p>
          </div>
        </div>
      </footer>

      <a
        className={`${styles.fab} ${showFab ? styles.fabOn : ''}`}
        href={WA_DUVIDA}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Falar connosco no WhatsApp"
        tabIndex={showFab ? 0 : -1}
      >
        <WhatsAppIcon />
        <span>Fale connosco</span>
      </a>
    </div>
  )
}
